import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Loader2, User, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
} from "@/app/admin/_common/ui/sheet";
import CuoButton from "@/app/admin/_common/CuoButton";
import ShowThumbnail from "@/app/admin/_common/ShowThumbnail";
import GuestService from "@/app/_services/GuestService";
import { formatShowDate, formatUtcDateTime } from "@/app/_utils/dayjs";
import { getSignupsTimestamps } from "@/app/_utils/guestSignups";
import GuestForm from "./GuestForm";

export default function GuestEditDialog({
  guest,
  open,
  onClose,
  getData,
}: {
  guest: any;
  open: boolean;
  onClose: () => void;
  getData: () => void;
}) {
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  // Keep rendering the last-known guest while `guest` goes null on close, so
  // the sheet's exit animation has content to animate out instead of the
  // whole tree unmounting instantly.
  const [lastGuest, setLastGuest] = useState(guest);

  useEffect(() => {
    if (guest) setLastGuest(guest);
  }, [guest]);

  // The list payload guest rows come from is trimmed (no phone/dob/state/notes),
  // so fetch the full guest record here for the form fields.
  const [guestDetails, setGuestDetails] = useState<any>(null);
  useEffect(() => {
    if (!guest?.id) {
      setGuestDetails(null);
      return;
    }
    setGuestDetails(null);
    GuestService.getGuest(guest.id).then(setGuestDetails);
  }, [guest?.id]);

  const displayGuest = guest ?? lastGuest;
  if (!displayGuest) return null;

  const fullGuest =
    guestDetails?.id === displayGuest.id ? { ...displayGuest, ...guestDetails } : null;

  const signupsTimestamps = getSignupsTimestamps(displayGuest.signups);
  const initials = `${displayGuest.first_name?.[0] ?? ""}${displayGuest.last_name?.[0] ?? ""}`.toUpperCase();

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        hideClose
        className="flex w-full flex-col gap-0 bg-white px-4 sm:max-w-[420px]"
        style={{ paddingTop: 20 }}
      >
        <SheetHeader className="space-y-0 border-b border-border pb-4 text-left">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex size-9 flex-none items-center justify-center rounded-full bg-muted text-[13px] font-semibold text-foreground">
                {initials || <User className="size-4 text-muted-foreground" />}
              </div>
              <div className="min-w-0">
                <SheetTitle className="m-0 min-w-0 truncate leading-none">
                  <span className="inline-flex items-center gap-1.5 align-middle">
                    {displayGuest.first_name} {displayGuest.last_name}
                    {displayGuest.num_shows >= 2 && (
                      <Star className="size-3.5 flex-none fill-amber-400 text-amber-400" />
                    )}
                  </span>
                </SheetTitle>
                <SheetDescription className="m-0 mt-0.5">Edit guest details</SheetDescription>
              </div>
            </div>
            <SheetClose className="flex size-6 flex-none shrink-0 items-center justify-center rounded-md text-muted-foreground ring-offset-background transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {displayGuest.num_shows > 0 && <LastShow guestId={displayGuest.id} onNavigate={onClose} />}
          {fullGuest ? (
            <GuestForm
              guest={fullGuest}
              onSaved={() => {
                getData();
                onClose();
              }}
              setDisableSubmitButton={setDisableSubmitButton}
            />
          ) : (
            <div className="flex h-[260px] items-center justify-center border-t border-border">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          )}
          <GuestPastSignups signups={signupsTimestamps} />
        </div>

        <SheetFooter className="flex-row justify-end gap-2 space-x-0 border-t border-border pt-3">
          <CuoButton variant="outline" label="Cancel" onClick={onClose} />
          <CuoButton
            variant="default"
            type="submit"
            form="edit-guest-form"
            label="Save"
            disabled={!fullGuest}
            disableOnAsync={disableSubmitButton}
          />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export function GuestPastSignups({ signups }: { signups: number[] }) {
  const recentSignups = [...signups].reverse().slice(0, 5);
  if (recentSignups.length === 0) return null;

  return (
    <div className="border-t border-border py-3">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Recent signups
      </div>
      <div className="flex flex-col gap-2">
        {recentSignups.map((signup, indx) => (
          <div key={indx} className="flex items-center justify-between gap-3 text-[12px]">
            <span className="text-foreground">
              {indx === signups.length - 1 ? "Initial Signup" : "Re-signup for Waitlist"}
            </span>
            <span className="flex-none text-muted-foreground">
              {formatUtcDateTime(signup, "ddd MMM D, YYYY h:mm a")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LastShow({ guestId, onNavigate }: { guestId: string; onNavigate: () => void }) {
  const [show, setShow] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    GuestService.getGuestLastShow(guestId)
      .then(setShow)
      .finally(() => setLoading(false));
  }, [guestId]);

  return (
    <div className="py-3">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Last show
      </div>
      {loading ? (
        <div className="flex h-[70px] items-center justify-center rounded-md border border-border">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : show ? (
        <div
          className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-2 hover:bg-muted/50"
          onClick={() => {
            if (!show?.order_id) return;
            onNavigate();
            navigate(`/admin/orders?orderId=${show.order_id}`);
          }}
        >
          <ShowThumbnail
            src={show.img_url}
            style={{ height: 56, width: 56, borderRadius: 6, flexShrink: 0 }}
          />
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-[13px] text-foreground">{show.title}</span>
            <span className="truncate text-[11px] text-muted-foreground">{show.location?.name}</span>
            <span className="text-[11px] text-muted-foreground">
              {formatShowDate(show.start, "ddd MMM D, YYYY")}
            </span>
          </div>
        </div>
      ) : (
        <div className="py-2 text-[12px] text-muted-foreground">No previous shows</div>
      )}
    </div>
  );
}
